import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  RestartAlt as ResetIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { apiGet, apiPut, apiPost, extractApiError } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { useNotification } from '../../contexts/NotificationContext';

// Friendly label for a member notification type key (e.g. payment_received -> "Payment Received").
const prettyType = (key) =>
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Email template management — the 21 notification types render through 6 editable
 * family LAYOUTS. An admin edits a family layout (subject + body HTML, Handlebars);
 * the edit affects every member notification of that family. Preview renders the
 * layout for a chosen member type as HTML in an iframe (no PDF/Puppeteer dependency).
 */
function EmailTemplateManagement() {
  const notify = useNotification();

  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Edit dialog state
  const [editing, setEditing] = useState(null); // the family list row being edited
  const [detail, setDetail] = useState(null); // GET /templates/:family payload
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [previewMember, setPreviewMember] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocusedRef = useRef('body'); // which field to insert tokens into

  // Preview is the email shown at natural size in a scroll box whose height matches the
  // editor column on the left (same Grid row + flex:1). The iframe is the ONLY scroll
  // surface — no zoom/scaling, no double scrollbar.

  const loadFamilies = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiGet('/email/templates');
      setFamilies(res.data || []);
    } catch (err) {
      logger.error('Failed to load email templates', err);
      setLoadError(extractApiError(err, 'Failed to load email templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  // ---- Edit dialog ----
  const openEditor = async (family) => {
    setEditing(family);
    setDetail(null);
    setDialogError(null);
    setPreviewHtml('');
    try {
      const res = await apiGet(`/email/templates/${family.template_family}`);
      const d = res.data;
      setDetail(d);
      setSubject(d.subject || '');
      setBodyHtml(d.body_html || '');
      setPreviewMember((d.members && d.members[0]) || '');
    } catch (err) {
      setDialogError(extractApiError(err, 'Failed to load template'));
    }
  };

  const closeEditor = () => {
    setEditing(null);
    setDetail(null);
    setSubject('');
    setBodyHtml('');
    setPreviewHtml('');
    setDialogError(null);
  };

  // Insert a {{token}} at the cursor of whichever field was last focused.
  const insertToken = (token) => {
    const snippet = `{{${token}}}`;
    const target = lastFocusedRef.current === 'subject' ? subjectRef.current : bodyRef.current;
    const setter = lastFocusedRef.current === 'subject' ? setSubject : setBodyHtml;
    const current = lastFocusedRef.current === 'subject' ? subject : bodyHtml;
    if (target && typeof target.selectionStart === 'number') {
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const next = current.slice(0, start) + snippet + current.slice(end);
      setter(next);
      // restore caret after the inserted token
      requestAnimationFrame(() => {
        try {
          target.focus();
          target.selectionStart = target.selectionEnd = start + snippet.length;
        } catch (e) { /* noop */ }
      });
    } else {
      setter(current + snippet);
    }
  };

  // Live preview (debounced) of the current draft for the selected member.
  const runPreview = useCallback(async () => {
    if (!editing || !previewMember) return;
    setPreviewLoading(true);
    try {
      const res = await apiPost(`/email/templates/${editing.template_family}/preview`, {
        memberType: previewMember,
        subject,
        body_html: bodyHtml,
      });
      setPreviewHtml(res.data?.html || '');
      setDialogError(null);
    } catch (err) {
      // Preview-time compile errors are useful inline feedback, not a toast.
      setDialogError(extractApiError(err, 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  }, [editing, previewMember, subject, bodyHtml]);

  useEffect(() => {
    if (!editing || !previewMember) return undefined;
    const id = setTimeout(runPreview, 500);
    return () => clearTimeout(id);
  }, [editing, previewMember, subject, bodyHtml, runPreview]);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await apiPut(`/email/templates/${editing.template_family}`, {
        subject,
        body_html: bodyHtml,
      });
      notify.success(`"${editing.label}" template saved`);
      closeEditor();
      loadFamilies();
    } catch (err) {
      setDialogError(extractApiError(err, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (family) => {
    try {
      await apiPost(`/email/templates/${family.template_family}/reset`, {});
      notify.success(`"${family.label}" reset to default`);
      if (editing && editing.template_family === family.template_family) {
        // reload the editor with the now-default content
        openEditor(family);
      }
      loadFamilies();
    } catch (err) {
      notify.error(extractApiError(err, 'Failed to reset template'));
    }
  };

  const restoreDefaultsInEditor = () => {
    if (!detail) return;
    setSubject(detail.default_subject || '');
    setBodyHtml(detail.default_body_html || '');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error" onClose={() => loadFamilies()}>{loadError}</Alert>;
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Template</TableCell>
              <TableCell>Used by</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {families.map((f) => (
              <TableRow key={f.template_family} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{f.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.description}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={(f.members || []).map(prettyType).join(', ')}>
                    <Chip size="small" label={`${f.member_count} notification${f.member_count === 1 ? '' : 's'}`} variant="outlined" />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {f.is_customised
                    ? <Chip size="small" color="primary" label="Customised" />
                    : <Chip size="small" label="Default" variant="outlined" />}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {f.is_customised && f.updated_at
                      ? `${new Date(f.updated_at).toLocaleDateString()}${f.updated_by ? ' · ' + f.updated_by : ''}`
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEditor(f)}>Edit</Button>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<ResetIcon />}
                    disabled={!f.is_customised}
                    onClick={() => handleReset(f)}
                  >
                    Reset
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit dialog */}
      <Dialog
        open={!!editing}
        onClose={closeEditor}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: '95vw', maxWidth: 1700, maxHeight: '92vh' } }}
      >
        <DialogTitle>
          Edit Template — {editing?.label}
          <Typography variant="caption" color="text.secondary" display="block">
            Editing this layout affects all {editing?.member_count} notification(s) in this group.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={2}>
              {/* Left: editor + field palette */}
              <Grid size={{ xs: 12, md: 6 }}>
                {dialogError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>{dialogError}</Alert>}

                <TextField
                  label="Subject"
                  fullWidth
                  size="small"
                  value={subject}
                  inputRef={subjectRef}
                  onFocus={() => { lastFocusedRef.current = 'subject'; }}
                  onChange={(e) => setSubject(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  label="Body (HTML, Handlebars)"
                  fullWidth
                  multiline
                  minRows={20}
                  value={bodyHtml}
                  inputRef={bodyRef}
                  onFocus={() => { lastFocusedRef.current = 'body'; }}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
                />

                <Typography variant="subtitle2" gutterBottom>Insert field</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Click to insert at the cursor (in the last focused field). Detail rows are filled
                  automatically per notification; layout tokens control the shared structure.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {(detail.fields || []).map((field) => (
                    <Tooltip key={field.token} title={`${field.label}${field.example ? ' — e.g. ' + field.example : ''}`}>
                      <Chip
                        size="small"
                        label={`{{${field.token}}}`}
                        onClick={() => insertToken(field.token)}
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                      />
                    </Tooltip>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />
                <Button size="small" startIcon={<ResetIcon />} onClick={restoreDefaultsInEditor}>
                  Restore default content (in editor)
                </Button>
              </Grid>

              {/* Right: preview — full-height flex column so the preview box stretches
                  to match the editor column height (no oversized empty grey area). */}
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">Preview as:</Typography>
                  <TextField
                    select
                    size="small"
                    value={previewMember}
                    onChange={(e) => setPreviewMember(e.target.value)}
                    sx={{ minWidth: 240 }}
                  >
                    {(detail.members || []).map((m) => (
                      <MenuItem key={m} value={m}>{prettyType(m)}</MenuItem>
                    ))}
                  </TextField>
                  {previewLoading && <CircularProgress size={18} />}
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    flex: 1,
                    minHeight: 300,
                    overflow: 'hidden', // the iframe is the only scroll surface (no double scrollbar)
                    bgcolor: '#f4f4f4',
                  }}
                >
                  {/* Email shown at natural size; the iframe fills the box (height matches the
                      editor column via the shared Grid row + flex:1) and is the single scroll
                      surface for a tall email. No zoom/scaling. */}
                  <iframe
                    title="Email preview"
                    srcDoc={previewHtml}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      display: 'block',
                    }}
                    sandbox=""
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !detail}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailTemplateManagement;
